import React from 'react';
import { Sparkles, Check, ExternalLink } from 'lucide-react';
import { TaskySelect } from '../tasky';
import type {
  LpDesign,
  LpGenerationStage,
  LpWorkflowProject,
} from '../../services/api/lpWorkflowService';

interface Props {
  value: LpDesign;
  project: LpWorkflowProject;
  busy: boolean;
  onChange: (value: LpDesign) => void;
  onGenerate: (
    stage: LpGenerationStage,
    sectionId?: string,
    designId?: string,
  ) => void;
  onBuild: (designId: string) => void;
  onPreview: (id?: string) => void;
  onAttach: (sectionId: string, key: string | null) => void;
  previews: Record<string, string>;
}

export default function LpWorkflowDesign({
  value,
  project,
  busy,
  onChange,
  onGenerate,
  onBuild,
  onPreview,
  onAttach,
  previews,
}: Props) {
  const selected = value.options.find((o) => o.id === value.selected_id);
  const status = selected
    ? project.design_candidates?.[selected.id]
    : undefined;
  const sections = project.workflow.outline?.sections ?? [];
  const planned = value.options.every((o) => !!o.sections?.length);
  const conceptEstimate =
    project.concept_credit_estimate ?? project.image_credit_estimate;
  const remainingConcepts = value.options.filter((o) => {
    const concept = project.design_candidates?.[o.id]?.concept;
    return !concept || concept.stale;
  }).length;
  const savedSelection =
    value.selected_id === project.workflow.design?.selected_id;

  return (
    <div className="lpw-form">
      <div className="lpw-design-process">
        <div>
          <b>全体デザインを比較</b>
          <p>商品素材からLP全体の完成イメージを作ります。</p>
        </div>
        <div>
          <b>各セクションの画像を生成</b>
          <p>選んだ全体画像と実物素材を参照して揃えます。</p>
        </div>
        <div>
          <b>LPを組み立て</b>
          <p>全体画像をもとに、文字とボタンを編集可能なHTMLにします。</p>
        </div>
      </div>
      {!planned && (
        <div className="lpw-note">
          上の「AIで提案を作り直す」で、全体デザイン画像から制作する新しいスタイル案を提案できます。現在の案はそのまま利用できます。
        </div>
      )}
      {!!project.reference_images.length && (
        <div className="lpw-product-materials">
          <strong>このLPで使う商品素材</strong>
          <div>
            {project.reference_images.map((ref, i) => (
              <a
                href={ref.url}
                target="_blank"
                rel="noreferrer"
                key={ref.s3_key}
              >
                <img src={ref.url} alt={`商品素材 ${i + 1}`} />
                <span>素材 {i + 1}</span>
              </a>
            ))}
          </div>
        </div>
      )}
      {planned && (
        <div className="lpw-concept-action">
          <div>
            <strong>まず、LP全体のデザイン画像を作成</strong>
            <p>
              全体画像は構図と見た目の見本です。完成LPには確定済みの文言が入ります。
            </p>
            <p className="lpw-muted">
              未生成・変更後の{remainingConcepts}案 ／ 約
              {remainingConcepts * conceptEstimate}
              クレジット。完成済みの画像は保持します。
            </p>
          </div>
          <button
            type="button"
            className="lpw-button"
            disabled={busy || remainingConcepts === 0}
            onClick={() => onGenerate('concepts')}
          >
            <Sparkles size={16} />
            全体デザイン画像を{remainingConcepts || value.options.length}案生成
          </button>
        </div>
      )}
      <div className="lpw-design-candidates">
        {value.options.map((option, index) => {
          const candidate = project.design_candidates?.[option.id];
          const concept = candidate?.concept;
          return (
            <article
              className={`lpw-option lpw-design-candidate ${option.id === value.selected_id ? 'selected' : ''}`}
              key={option.id}
            >
              <label className="lpw-option-heading">
                <input
                  type="radio"
                  name="design"
                  checked={option.id === value.selected_id}
                  onChange={() =>
                    onChange({ ...value, selected_id: option.id })
                  }
                />
                <span>デザイン案 {index + 1}</span>
                {option.id === value.selected_id && <Check size={16} />}
              </label>
              <h3>{option.name}</h3>
              {concept ? (
                <a
                  className="lpw-concept-image"
                  href={concept.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${option.name}の全体デザイン画像を拡大`}
                >
                  <img
                    src={concept.url}
                    alt={`${option.name}のLP全体デザイン`}
                  />
                  <span>
                    <ExternalLink size={14} />
                    全体画像を拡大
                  </span>
                </a>
              ) : (
                <div className="lpw-concept-empty">
                  <Sparkles size={24} />
                  <span>
                    全体デザイン画像を生成すると
                    <br />
                    ここで比較できます
                  </span>
                </div>
              )}
              {concept?.stale && (
                <p className="lpw-badge warning">
                  内容が変更されています。全体画像を更新してください。
                </p>
              )}
              <p className="lpw-muted">{option.reason}</p>
              <details className="lpw-direction-details">
                <summary>デザインの方向性を調整</summary>
                <label className="lpw-field">
                  全体の画像指示
                  <textarea
                    rows={5}
                    maxLength={3000}
                    value={option.image_direction}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        options: value.options.map((o) =>
                          o.id === option.id
                            ? { ...o, image_direction: e.target.value }
                            : o,
                        ),
                      })
                    }
                  />
                </label>
                <div className="lpw-palette">
                  {(['primary', 'accent', 'base'] as const).map((field) => (
                    <label key={field}>
                      {
                        {
                          primary: '基調色',
                          accent: 'アクセント',
                          base: '背景',
                        }[field]
                      }
                      <input
                        type="color"
                        value={option[field]}
                        onChange={(e) =>
                          onChange({
                            ...value,
                            options: value.options.map((o) =>
                              o.id === option.id
                                ? { ...o, [field]: e.target.value }
                                : o,
                            ),
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </details>
              {planned && (
                <div className="lpw-candidate-build">
                  <button
                    type="button"
                    className="lpw-button"
                    disabled={busy || !concept || concept.stale}
                    onClick={() => onBuild(option.id)}
                  >
                    <Sparkles size={16} />
                    この内容で作る
                  </button>
                  <p className="lpw-muted">
                    {candidate?.code_ready
                      ? '作成済み。押すと変更分だけ作り直します。'
                      : `セクション画像${Math.max(0, sections.length - (candidate?.images_ready ?? 0))}枚を自動生成してLPを組み立てます（約${Math.max(0, sections.length - (candidate?.images_ready ?? 0)) * project.image_credit_estimate}クレジット）。`}
                  </p>
                </div>
              )}
              {planned && concept && (
                <button
                  type="button"
                  className="lpw-button secondary small"
                  disabled={busy}
                  onClick={() => onGenerate('concept', undefined, option.id)}
                >
                  この全体画像を再生成（約{conceptEstimate}クレジット）
                </button>
              )}
              {(!planned || candidate?.code_ready) && (
                <button
                  type="button"
                  className="lpw-button secondary"
                  disabled={busy}
                  onClick={() => onPreview(option.id)}
                >
                  この案のLP全体を確認
                </button>
              )}
              {previews[option.id] && (
                <iframe
                  sandbox=""
                  title={`${option.name}のLPプレビュー`}
                  srcDoc={previews[option.id]}
                  className="lpw-fv-preview lpw-candidate-preview"
                />
              )}
            </article>
          );
        })}
      </div>
      {selected && (
        <div className="lpw-section-artworks">
          <h3>採用案のセクション画像</h3>
          {!savedSelection && (
            <p className="lpw-note">
              案を保存すると、その案のセクション画像が表示されます。
            </p>
          )}
          {savedSelection &&
            sections.map((section) => {
              const asset = project.workflow.assets[section.id];
              const plan = selected.sections?.find((s) => s.id === section.id);
              return (
                <article className="lpw-section-card" key={section.id}>
                  <div className="lpw-section-heading">
                    <strong>{section.title}</strong>
                    {asset?.stale && (
                      <span className="lpw-badge warning">
                        画像の更新が必要
                      </span>
                    )}
                  </div>
                  <div className="lpw-image-row">
                    {asset ? (
                      <img src={asset.url} alt={section.title} />
                    ) : (
                      <div className="lpw-image-empty">
                        {planned ? '全体デザインを選んで生成' : '画像は未設定'}
                      </div>
                    )}
                    <div className="lpw-image-controls">
                      {plan && <p className="lpw-muted">{plan.image_prompt}</p>}
                      <button
                        type="button"
                        className="lpw-button secondary"
                        disabled={
                          busy ||
                          (planned &&
                            (!status?.concept || status.concept.stale))
                        }
                        onClick={() => onGenerate('image', section.id)}
                      >
                        <Sparkles size={16} />
                        {asset ? 'この画像を再生成' : 'この画像を生成'}（約
                        {project.image_credit_estimate}クレジット）
                      </button>
                      <label className="lpw-field">
                        商品素材をそのまま使う
                        <TaskySelect
                          className="font-normal"
                          value=""
                          onChange={(e) => {
                            if (e.target.value)
                              onAttach(section.id, e.target.value);
                          }}
                        >
                          <option value="">素材から選ぶ</option>
                          {project.reference_images.map((ref, i) => (
                            <option key={ref.s3_key} value={ref.s3_key}>
                              素材 {i + 1}
                            </option>
                          ))}
                        </TaskySelect>
                      </label>
                      {asset && (
                        <button
                          type="button"
                          className="lpw-button small"
                          onClick={() => onAttach(section.id, null)}
                        >
                          画像を外す
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
        </div>
      )}
    </div>
  );
}
